if (!customElements.get('editorial-slideshow')) {
  customElements.define('editorial-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-editorial-rail]');
      this.slides = [...this.querySelectorAll('[data-editorial-slide]')];
      this.prevButton = this.querySelector('[data-editorial-prev]');
      this.nextButton = this.querySelector('[data-editorial-next]');
      this.status = this.querySelector('[data-editorial-status]');
      this.index = 0;
      this.wheelAccumulator = 0;
      this.wheelResetTimer = null;
      this.wheelSnapRestoreTimer = null;
      this.boundaryWheelDirection = 0;
      this.boundaryWheelTicks = 0;
      this.lastWheelStepAt = 0;
      this.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-editorial-slide]');
        const link = slide?.dataset.editorialLink;
        if (!link || event.target.closest('a, button, input, select, textarea, [data-editorial-floating-reviews], [data-editorial-reviews], [data-editorial-prev], [data-editorial-next]')) return;
        window.location.href = link;
      });
      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));
      this.rail?.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-editorial-slide]');
        if (slide) this.goTo(this.slides.indexOf(slide));
      });
      this.update();
      this.start();
      this.initReviewsBadge();
      this.initFloatingReviews();
    }

    initReviewsBadge() {
      const badge = this.querySelector('[data-editorial-reviews]');
      if (!badge) return;

      const source = this.querySelector('[data-editorial-reviews-source]');
      const starsEl = source?.querySelector('.jdgm-rating__stars[data-score]');
      const countEl = source?.querySelector('.jdgm-rating__count[data-value]');

      let rating = starsEl ? parseFloat(starsEl.dataset.score) : NaN;
      let count = countEl ? parseInt(countEl.dataset.value, 10) : NaN;

      if ((isNaN(rating) || isNaN(count)) && window.jdgm?.data?.storeSummaryWidget) {
        const summary = window.jdgm.data.storeSummaryWidget;
        if (isNaN(rating)) rating = parseFloat(summary.average_rating);
        if (isNaN(count)) count = parseInt(summary.number_of_reviews, 10);
      }

      if (isNaN(rating) || isNaN(count) || !count) return;

      const fill = badge.querySelector('.editorial-slideshow__reviews-stars-fill');
      if (fill) fill.style.width = `${Math.max(0, Math.min(100, (rating / 5) * 100))}%`;

      const text = badge.querySelector('[data-editorial-reviews-text]');
      if (text) text.textContent = `${rating.toFixed(1)}/5 from ${count.toLocaleString()}+ verified customers`;

      badge.hidden = false;
    }

    initFloatingReviews() {
      const mount = this.querySelector('[data-editorial-floating-reviews]');
      if (!mount) return;
      if (window.matchMedia('(max-width: 699px)').matches) return;

      const spawnInterval = (Number(mount.dataset.interval) || 5) * 1000;
      const displayDuration = (Number(mount.dataset.display) || 6) * 1000;
      const speedRatio = Math.max(0.5, Math.min(1.5, (Number(mount.dataset.speed) || 100) / 100));
      const maxCards = Math.max(1, Math.min(8, Number(mount.dataset.maxCards) || 5));
      const purchaseFrequency = Math.max(2, Math.min(6, Number(mount.dataset.purchaseFrequency) || 3));
      const purchaseIcon = mount.dataset.purchaseIcon || 'truck';
      const fallbackLink = mount.dataset.reviewLink || 'https://judge.me/reviews/stores/deskhero';
      const localSource = this.querySelector('[data-editorial-reviews-source]');
      const reviewsEnabled = mount.dataset.reviewsEnabled === 'true';
      const purchasesEnabled = mount.dataset.purchasesEnabled === 'true';
      const purchaseReviews = purchasesEnabled ? this.extractPurchaseCards(mount) : [];

      const poll = (attempts = 0) => {
        const localReviews = reviewsEnabled ? this.extractCarouselReviews(localSource) : [];
        const cardReviews = reviewsEnabled ? (localReviews.length ? localReviews : this.extractCarouselReviews(document)) : [];
        const floatingCards = [...cardReviews, ...purchaseReviews];
        if (floatingCards.length) {
          this.startFloatingReviews(mount, floatingCards, spawnInterval, displayDuration, speedRatio, fallbackLink, maxCards, purchaseFrequency, purchaseIcon);
          return;
        }

        const gallery = reviewsEnabled ? window.jdgm?.data?.storeSummaryWidget?.photo_gallery : null;
        if (Array.isArray(gallery) && gallery.length) {
          const reviews = gallery
            .map((review) => ({
              type: 'review',
              image: review.pictures_urls?.small,
              name: review.reviewer_name || 'Verified buyer',
              title: this.stripHtml(review.title || ''),
              text: this.stripHtml(review.body_html || review.title || ''),
              rating: Math.round(Number(review.rating)) || 0,
            }))
            .filter((review) => review.image && review.text);
          const fallbackCards = [...reviews, ...purchaseReviews];
          if (fallbackCards.length) { this.startFloatingReviews(mount, fallbackCards, spawnInterval, displayDuration, speedRatio, fallbackLink, maxCards, purchaseFrequency, purchaseIcon); return; }
        }

        if (attempts > 30) return;
        setTimeout(() => poll(attempts + 1), 500);
      };
      poll();
    }

    extractCarouselReviews(source) {
      if (!source) return [];
      return [...source.querySelectorAll('.jdgm-carousel-item')]
        .map((item) => {
          const image = item.querySelector('.jdgm-carousel-item__product-image');
          const name = item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.trim();
          const title = item.querySelector('.jdgm-carousel-item__review-title')?.textContent.trim();
          const body = item.querySelector('.jdgm-carousel-item__review-body')?.textContent.trim();
          const ratingEl = item.querySelector('.jdgm-carousel-item__review-rating');
          const rating = ratingEl ? ratingEl.querySelectorAll('.jdgm-star.jdgm--on').length : 0;
          return {
            type: 'review',
            image: image?.getAttribute('src') || image?.dataset.src,
            name: name || 'Verified buyer',
            title: title || '',
            text: body || title || '',
            rating,
            url: item.querySelector('a[href]')?.href || '',
          };
        })
        .filter((review) => review.image && review.text);
    }

    stripHtml(str) { return String(str).replace(/<[^>]*>/g, '').trim(); }

    readJsonScript(root, selector, fallback) {
      try {
        return JSON.parse(root.querySelector(selector)?.textContent || JSON.stringify(fallback));
      } catch (error) {
        return fallback;
      }
    }

    formatCustomerName(name) {
      const normalizedName = `${name || ''}`.trim();
      if (!normalizedName) return 'there';
      return normalizedName
        .toLowerCase()
        .split(/([\s'-]+)/)
        .map((part) => (/^[\s'-]+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join('');
    }

    getOrderStage(entry) {
      const fulfillmentStatus = `${entry.fulfillment_status || ''}`.trim().toUpperCase().replace(/[\s-]+/g, '_');
      const deliveryStatus = `${entry.delivery_status || ''}`.trim().toUpperCase().replace(/[\s-]+/g, '_');
      if (deliveryStatus === 'DELIVERED') return 'delivered';
      if (deliveryStatus === 'OUT_FOR_DELIVERY') return 'out_for_delivery';
      if (deliveryStatus === 'IN_TRANSIT') return 'in_transit';
      if (!fulfillmentStatus && !deliveryStatus) return 'created';
      return 'created';
    }

    isInsuranceEntry(entry) {
      const productTitle = `${entry.product_title || ''}`.trim().toUpperCase();
      return productTitle === 'SHIPPING PROTECTION' || productTitle === 'SIGNATURE REQUIRED';
    }

    getPurchaseMessage(entry, messagePools) {
      const fallbackDisplayText = `${entry.display_text || ''}`.trim();
      const stage = this.isInsuranceEntry(entry) ? 'insurance' : this.getOrderStage(entry);
      const messages = Array.isArray(messagePools?.[stage]) ? messagePools[stage].filter(Boolean) : [];
      const message = messages.length ? messages[Math.floor(Math.random() * messages.length)] : fallbackDisplayText;
      return `${message || fallbackDisplayText}`.replace(/\{\s*FirstName\s*\}/g, this.formatCustomerName(entry.first_name));
    }

    extractPurchaseCards(mount) {
      const entries = this.readJsonScript(mount, '[data-editorial-purchase-entries]', []);
      const messagePools = this.readJsonScript(mount, '[data-editorial-purchase-message-pools]', {});
      return entries
        .filter((entry) => entry && entry.product_title)
        .sort((entryA, entryB) => (entryB.updated_at_ts || 0) - (entryA.updated_at_ts || 0))
        .map((entry) => ({
          type: 'purchase',
          image: entry.image,
          title: entry.product_title || '',
          text: this.getPurchaseMessage(entry, messagePools),
          name: '',
          rating: 0,
          url: entry.product_url || '#',
          entryHandle: entry.entry_handle || '',
          productHandle: entry.product_handle || '',
          productId: entry.product_id || '',
          productTitle: entry.product_title || '',
        }))
        .filter((entry) => entry.image && entry.text && entry.title);
    }

    startFloatingReviews(mount, reviews, spawnInterval, displayDuration, speedRatio, fallbackLink, maxCards = 5, purchaseFrequency = 3, purchaseIcon = 'truck') {
      const allCards = [...reviews];
      const reviewCards = allCards.filter((card) => card.type !== 'purchase');
      const purchaseCards = allCards.filter((card) => card.type === 'purchase');
      const reviewPool = [...reviewCards];
      const purchasePool = [...purchaseCards];
      const fallbackPool = [...allCards];
      const activeLefts = [];
      let spawnCount = 0;

      const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));

      const takeRandomCard = (sourcePool, sourceCards) => {
        if (!sourcePool.length) sourcePool.push(...sourceCards);
        return sourcePool.splice(Math.floor(Math.random() * sourcePool.length), 1)[0];
      };

      const getPurchaseIcon = () => {
        if (purchaseIcon === 'none') return '';
        const icons = {
          truck: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 7h4l3 4v5h-2.1a3 3 0 0 1-5.8 0H9.9a3 3 0 0 1-5.8 0H2V5h12v2Zm0 2v5h1.2a3 3 0 0 1 2.6 0H19v-2.3L17 9h-3ZM4 7v7h.2a3 3 0 0 1 5.6 0H12V7H4Zm3 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm9 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/></svg>',
          package: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2 9 5v10l-9 5-9-5V7l9-5Zm0 2.3L6.2 7.5 12 10.7l5.8-3.2L12 4.3ZM5 9.2v6.6l6 3.3v-6.6L5 9.2Zm8 9.9 6-3.3V9.2l-6 3.3v6.6Z"/></svg>',
          check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.4 16.6 4.8 12l1.4-1.4 3.2 3.2 8.4-8.4L19.2 7 9.4 16.6Z"/></svg>',
        };
        return `<span class="editorial-slideshow__floating-purchase-icon">${icons[purchaseIcon] || icons.truck}</span>`;
      };

      const pickLeftPercent = () => {
        for (let attempt = 0; attempt < 8; attempt++) {
          const candidate = 4 + Math.random() * 72;
          if (activeLefts.every((used) => Math.abs(used - candidate) > 22)) return candidate;
        }
        return 4 + Math.random() * 72;
      };

      const spawn = (initialProgress = 0, preferredType = '') => {
        if (!this.isConnected || document.hidden || !this.floatingInView) return;
        if (mount.querySelectorAll('.editorial-slideshow__floating-card').length >= maxCards) return;

        spawnCount += 1;
        const shouldShowPurchase = purchaseCards.length > 0 && (preferredType === 'purchase' || spawnCount % purchaseFrequency === 0);
        const review = shouldShowPurchase
          ? takeRandomCard(purchasePool, purchaseCards)
          : reviewCards.length
            ? takeRandomCard(reviewPool, reviewCards)
            : takeRandomCard(fallbackPool, allCards);

        const cardLink = review.url || fallbackLink;
        const card = document.createElement('a');
        card.className = `editorial-slideshow__floating-card editorial-slideshow__floating-card--${review.type || 'review'}`;
        card.href = cardLink;
        card.setAttribute('aria-label', review.type === 'purchase' ? `View ${review.title}` : `Read review by ${review.name}`);
        if (review.type === 'purchase') {
          card.dataset.popupEntryHandle = review.entryHandle || '';
          card.dataset.popupProductHandle = review.productHandle || '';
          card.dataset.popupProductId = review.productId || '';
          card.dataset.popupProductTitle = review.productTitle || '';
          card.addEventListener('click', () => this.trackPurchaseCardClick(card), { once: true });
        }
        const leftPercent = pickLeftPercent();
        card.style.left = `${leftPercent}%`;
        activeLefts.push(leftPercent);

        const mountHeight = mount.clientHeight || 400;
        const cardHeight = Math.min(Number.parseFloat(getComputedStyle(mount).getPropertyValue('--floating-avatar-size')) || 46, 60) + 28;
        const rise = Math.round(mountHeight + cardHeight + 40);
        const drift = Math.round((Math.random() - 0.5) * Math.min(120, mount.clientWidth * 0.1));
        const duration = Math.round((displayDuration * (0.9 + Math.random() * 0.35)) / speedRatio);
        card.style.setProperty('--floating-rise', `-${rise}px`);
        card.style.setProperty('--floating-drift', `${drift}px`);
        card.style.setProperty('--floating-scale', (0.96 + Math.random() * 0.08).toFixed(2));
        card.style.animationDuration = `${duration}ms`;
        card.style.animationDelay = `${Math.round(-duration * initialProgress)}ms`;
        const starsHtml = review.rating > 0
          ? `<span class="editorial-slideshow__floating-stars" aria-hidden="true">${'★'.repeat(review.rating)}${'☆'.repeat(5 - review.rating)}</span>`
          : '';
        const titleHtml = review.title ? `<span class="editorial-slideshow__floating-title">${escapeHtml(review.title)}</span>` : '';
        const nameHtml = review.name ? `<span class="editorial-slideshow__floating-name">${escapeHtml(review.name)}</span>` : '';
        const purchaseIconHtml = review.type === 'purchase' ? getPurchaseIcon() : '';
        card.innerHTML = `
          <img class="editorial-slideshow__floating-avatar" src="${review.image}" alt="" loading="lazy">
          <div class="editorial-slideshow__floating-copy">
            ${starsHtml}
            ${titleHtml}
            <span class="editorial-slideshow__floating-text">${escapeHtml(review.text)}</span>
            ${nameHtml}
          </div>
          ${purchaseIconHtml}`;
        mount.appendChild(card);

        setTimeout(() => {
          card.remove();
          const index = activeLefts.indexOf(leftPercent);
          if (index !== -1) activeLefts.splice(index, 1);
        }, Math.round(duration * (1 - initialProgress)) + 100);
      };

      const start = () => {
        if (this.floatingTimer || !this.floatingInView || document.hidden) return;
        const activeCards = mount.querySelectorAll('.editorial-slideshow__floating-card').length;
        if (!activeCards) {
          spawn(0.08);
          if (maxCards > 1) spawn(0.4);
          if (maxCards > 2) spawn(0.72, purchaseCards.length ? 'purchase' : '');
        }

        // Keep about three cards in flight. The merchant's interval remains the
        // maximum gap, while longer display times automatically increase density.
        const cadence = Math.min(spawnInterval, displayDuration / 3);
        this.floatingTimer = setInterval(spawn, cadence);
      };

      const pause = () => {
        clearInterval(this.floatingTimer);
        this.floatingTimer = null;
      };

      this.floatingObserver = new IntersectionObserver(([entry]) => {
        this.floatingInView = entry.isIntersecting;
        entry.isIntersecting ? start() : pause();
      }, { threshold: 0.08 });
      this.floatingObserver.observe(this);

      this.onVisibilityChange = () => document.hidden ? pause() : start();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    trackPurchaseCardClick(card) {
      const payload = {
        entryHandle: card.dataset.popupEntryHandle || null,
        productHandle: card.dataset.popupProductHandle || null,
        productId: card.dataset.popupProductId || null,
        productTitle: card.dataset.popupProductTitle || null,
        clickedAt: new Date().toISOString(),
      };
      try {
        localStorage.setItem('deskhero:sales-popup-attribution', JSON.stringify(payload));
      } catch (error) {
      }
      const analyticsPayload = {
        entry_handle: payload.entryHandle,
        product_handle: payload.productHandle,
        product_id: payload.productId,
        product_title: payload.productTitle,
      };
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('deskhero:sales_popup_clicked', analyticsPayload);
      }
      document.dispatchEvent(new CustomEvent('deskhero:sales_popup_clicked', { detail: analyticsPayload }));
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.wheelResetTimer);
      clearTimeout(this.wheelSnapRestoreTimer);
      clearInterval(this.floatingTimer);
      this.floatingObserver?.disconnect();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    goTo(index) {
      if (!this.slides.length) return;
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[this.index];
      if (this.rail && slide) {
        this.rail.scrollTo({ left: slide.offsetLeft - this.rail.offsetLeft, behavior: 'smooth' });
      }
      this.update();
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const railLeft = this.rail.getBoundingClientRect().left;
        let closest = 0;
        let distance = Infinity;
        this.slides.forEach((slide, index) => {
          const nextDistance = Math.abs(slide.getBoundingClientRect().left - railLeft);
          if (nextDistance < distance) { distance = nextDistance; closest = index; }
        });
        this.index = closest;
        this.update();
      });
    }

    onWheel(event) {
      if (event.ctrlKey || !this.rail || this.slides.length < 2) return;

      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (!delta) return;

      const maxScrollLeft = this.rail.scrollWidth - this.rail.clientWidth;
      const atStart = this.rail.scrollLeft <= 1;
      const atEnd = this.rail.scrollLeft >= maxScrollLeft - 1;
      const atBoundary = (delta < 0 && atStart) || (delta > 0 && atEnd);
      if (atBoundary) {
        const direction = delta > 0 ? 1 : -1;
        if (this.boundaryWheelDirection !== direction) {
          this.boundaryWheelDirection = direction;
          this.boundaryWheelTicks = 0;
        }
        this.boundaryWheelTicks += 1;
        if (this.boundaryWheelTicks <= 2) event.preventDefault();
        return;
      }

      this.boundaryWheelDirection = 0;
      this.boundaryWheelTicks = 0;

      event.preventDefault();
      this.rail.classList.add('is-wheel-scrolling');
      clearTimeout(this.wheelSnapRestoreTimer);
      this.wheelSnapRestoreTimer = setTimeout(() => {
        this.rail.classList.remove('is-wheel-scrolling');
      }, 280);

      const slideDistance = this.slides[0]?.getBoundingClientRect().width || this.rail.clientWidth;
      const direction = delta > 0 ? 1 : -1;
      this.rail.scrollBy({ left: direction * slideDistance / 3, behavior: 'smooth' });
    }

    update() {
      if (this.status) this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
      if (this.prevButton) this.prevButton.disabled = this.index === 0;
      if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
    }

    start() {
      const delay = Number(this.getAttribute('autoplay'));
      if (!delay || this.slides.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return;
      this.stop();
      this.timer = setInterval(() => this.goTo(this.index === this.slides.length - 1 ? 0 : this.index + 1), delay);
    }

    stop() { clearInterval(this.timer); }
  });
}
